import { marketingAnchors, marketingCta, marketingHero } from '~/marketing/landingContent';

import { marketingAppDownloadVisible } from '../config';

import type {
  BootstrapResponse,
  HeroVariant,
  ModuleFlags,
  PresenceHeroContent,
} from '~/marketing/presenceContract';
import type { HandlersPresenceHeroBlock } from '~/api/models/HandlersPresenceHeroBlock';
import { heroBlockToPartial } from './presenceOpenApiMappers';

const DEFAULT_RESPONSES: string[] = [
  'Started cooking again instead of ordering out.',
  'Found a coffee shop that finally feels like mine.',
  'Went for a walk without my phone and it helped more than I expected.',
];

// Not phrased as live activity. StatTicker wraps this in a pulsing dot and aria-live="polite", so an
// assertion about what is happening right now would be live-activity chrome around a constant — the
// same thing the homepage's own copy already refuses to do.
const GENERIC_ACTIVITY_FALLBACK = 'Opening city by city.';

const downloadAnchorHref = `#${marketingAnchors.download}`;
const waitlistAnchorHref = `#${marketingAnchors.waitlist}`;

function heroPrimaryCta(): { label: string; href: string } {
  if (marketingAppDownloadVisible()) {
    return { label: marketingCta.downloadKynward, href: downloadAnchorHref };
  }
  return { label: marketingCta.joinFullReleaseList, href: waitlistAnchorHref };
}

function controlHero(): PresenceHeroContent {
  return {
    eyebrow: marketingHero.kicker,
    headline: marketingHero.h1,
    subhead: marketingHero.sub,
    primaryCta: heroPrimaryCta(),
    trustLine: marketingHero.trust,
    cardEyebrow: marketingHero.momentEyebrow,
    prompt: marketingHero.momentQuote,
    responses: [],
    cardNote: marketingHero.momentNote,
  };
}

function variantAHero(): PresenceHeroContent {
  return {
    eyebrow: marketingHero.kicker,
    headline: marketingHero.h1,
    subhead: marketingHero.sub,
    primaryCta: heroPrimaryCta(),
    trustLine: marketingHero.trust,
    cardEyebrow: 'A moment from a Circle',
    prompt: 'What’s one thing that made your week a little better?',
    responses: DEFAULT_RESPONSES,
    cardNote: 'Not a performance — just a better way to start talking.',
  };
}

function variantBHero(): PresenceHeroContent {
  return {
    eyebrow: 'Private beta · city by city',
    headline: marketingHero.h1,
    subhead: marketingHero.sub,
    primaryCta: heroPrimaryCta(),
    trustLine: marketingHero.trust,
    cardEyebrow: 'Circle prompt',
    prompt: 'What’s something you’re looking forward to this month?',
    promptCardLabel: 'Circle prompt',
    supportingLine: 'A few people. A little room. No pressure to impress.',
  };
}

function variantCHero(): PresenceHeroContent {
  return {
    eyebrow: marketingHero.kicker,
    headline: marketingHero.h1,
    subhead: marketingHero.sub,
    primaryCta: heroPrimaryCta(),
    trustLine: marketingHero.trust,
    cardEyebrow: marketingHero.momentEyebrow,
    prompt: marketingHero.momentQuote.replace(/^[\u201C""]|[\u201D""]$/g, ''),
    cardNote: marketingHero.momentNote,
    activityLine: GENERIC_ACTIVITY_FALLBACK,
  };
}

export function getStaticHeroForVariant(v: HeroVariant): PresenceHeroContent {
  if (v === 'control') return controlHero();
  if (v === 'A') return variantAHero();
  if (v === 'B') return variantBHero();
  if (v === 'C') return variantCHero();
  return controlHero();
}

/**
 * Merges API hero block (OpenAPI) with static fallbacks for any missing fields.
 */
export function mergeHeroContent(
  variant: HeroVariant,
  fromApi: HandlersPresenceHeroBlock | undefined
): PresenceHeroContent {
  const base = getStaticHeroForVariant(variant);
  if (!fromApi) return base;
  const partial = heroBlockToPartial(fromApi);
  return {
    ...base,
    ...partial,
    primaryCta: partial.primaryCta ?? base.primaryCta,
    secondaryCta: partial.secondaryCta ?? base.secondaryCta,
  };
}

export function mergeBootstrap(
  fromApi: BootstrapResponse | null,
  variant: HeroVariant
): { hero: PresenceHeroContent; modules: ModuleFlags } {
  if (!fromApi || fromApi.disabled) {
    return {
      hero: getStaticHeroForVariant(variant),
      modules: {
        momentFromCircle: true,
        recentFromCircles: true,
        ambientProofBar: true,
      },
    };
  }
  return {
    hero: mergeHeroContent(variant, fromApi.hero),
    modules: {
      momentFromCircle: fromApi.modules?.momentFromCircle ?? true,
      recentFromCircles: fromApi.modules?.recentFromCircles ?? true,
      ambientProofBar: fromApi.modules?.ambientProofBar ?? true,
    },
  };
}

export { DEFAULT_RESPONSES, GENERIC_ACTIVITY_FALLBACK };
